import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import ShowChart from '@mui/icons-material/ShowChart';
import AccountBalance from '@mui/icons-material/AccountBalance';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import { useAuth } from 'hooks';
import { TuTabList } from 'components/common/TuTabList/TuTabList';
import { TuTab } from 'components/common/TuTab/TuTab';
import { TuTabPanel } from 'components/common/TuTabPanel/TuTabPanel';
import { EfficiencyAccounts } from './tabs/EfficiencyAccounts/EfficiencyAccounts';
import { EfficiencyRank } from './tabs/EfficiencyRank/EfficiencyRank';
import { EfficiencyTrend } from './tabs/EfficiencyTrend/EfficiencyTrend';
import styles from './Efficiency.module.scss';

interface EfficiencyProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

export const Efficiency = ({ viewAsSupplier }: EfficiencyProps) => {
    const { user } = useAuth();
    const location = useLocation();
    const [currentView, setCurrentView] = useState<string>('trends');
    const [supplierId, setSupplierId] = useState<string | undefined>(
        viewAsSupplier
    );

    const tabPanelStyles = {
        height: 'calc(100% - 72px)',
        padding: '15px 0',
    };

    const changeTab = (_: React.SyntheticEvent, tabName: string) => {
        setCurrentView(tabName);
        if (!viewAsSupplier)
            window.history.replaceState(null, '', `/efficiency/${tabName}`);
        else
            window.history.replaceState(
                null,
                '',
                `/supplier-details/${viewAsSupplier}/efficiency/${tabName}`
            );
    };

    // Change tabs.
    useEffect(() => {
        const urlParts = location.pathname.split('/');
        let currentTab = urlParts.pop() ?? '';

        const planningTabs = ['trends', 'accounts', 'rank'];
        // If the last part of the URL does not match a valid tab/sub page, default to the list tab.
        if (!planningTabs.includes(currentTab)) {
            currentTab = 'trends';
        }

        setCurrentView(currentTab);
    }, [location.pathname]);

    useEffect(() => {
        setSupplierId(viewAsSupplier ?? user?.protected?.supplierId);
    }, [viewAsSupplier, user?.protected?.supplierId]);

    return (
        <div data-testid="Efficiency" className={styles.container}>
            <Box
                sx={{
                    width: '100%',
                    typography: 'body1',
                    height: '100%',
                    position: 'relative',
                }}
            >
                <TabContext value={currentView}>
                    <Box>
                        <TuTabList
                            variant="scrollable"
                            scrollButtons="auto"
                            onChange={changeTab}
                            aria-label="Efficiency Tabs"
                        >
                            <TuTab
                                label="Trends"
                                value="trends"
                                icon={<ShowChart />}
                                data-testid="TrendsTab"
                            />
                            <TuTab
                                label="Accounts"
                                value="accounts"
                                icon={<AccountBalance />}
                                data-testid="AccountsTab"
                            />
                            <TuTab
                                label="Rank"
                                value="rank"
                                icon={<EmojiEvents />}
                                data-testid="RankTab"
                            />
                        </TuTabList>
                    </Box>
                    <TuTabPanel value="trends" sx={{ ...tabPanelStyles }}>
                        <EfficiencyTrend supplierId={supplierId} />
                    </TuTabPanel>
                    <TuTabPanel value="accounts" sx={{ ...tabPanelStyles }}>
                        <EfficiencyAccounts supplierId={supplierId} />
                    </TuTabPanel>
                    <TuTabPanel value="rank" sx={{ ...tabPanelStyles }}>
                        <EfficiencyRank supplierId={supplierId} />
                    </TuTabPanel>
                </TabContext>
            </Box>
        </div>
    );
};
